import { AAWorkbenchNotificationsService } from '@amzn/aa-workbench-notification-service-client'

import { APP_CONFIG } from '../config.app'
import { Authenticator } from './Authenticator'
import { retryAuth } from './AxiosInterceptor'

export interface BaseEntity {
    entityType: string
    entityId: string
}

export interface NotificationServiceError extends Error {
    type: string
    message: string
}

export class NotificationServiceClient {
    private static client: AAWorkbenchNotificationsService | undefined

    private static getClient(): AAWorkbenchNotificationsService {
        if (!this.client) {
            this.client = new AAWorkbenchNotificationsService({
                endpoint: APP_CONFIG.notificationServiceBaseUrl,
            })

            this.client.middlewareStack.add(
                (next) => async (args) => {
                    const userSession = await retryAuth(() => Authenticator.getValidUserSession())
                    const token = userSession.getIdToken().getJwtToken()
                    const request = args.request as { headers: Record<string, string> }
                    request.headers = {
                        ...request.headers,
                        Authorization: token,
                    }
                    return next(args)
                },
                { step: 'finalizeRequest', name: 'cognitoAuthMiddleware' }
            )
        }
        return this.client
    }

    public static async followEntity(props: {
        entityType: string
        entityId: string
        entityName: string
    }): Promise<void> {
        try {
            await this.getClient().followEntity({ ...props })
        } catch (e: unknown) {
            console.error(e)
            throw this.handleError(e)
        }
    }

    public static async unfollowEntity(props: BaseEntity): Promise<void> {
        try {
            await this.getClient().unfollowEntity({ ...props })
        } catch (e: unknown) {
            console.error(e)
            throw this.handleError(e)
        }
    }

    public static async checkFollowingEntity(props: BaseEntity): Promise<boolean> {
        try {
            const response = await this.getClient().checkFollowingEntity({ ...props })
            return response.isFollowing || false
        } catch (e: unknown) {
            console.error(e)
            throw this.handleError(e)
        }
    }

    public static async checkFollowsBatch(props: {
        entities: BaseEntity[]
    }): Promise<BaseEntity[]> {
        try {
            const response = await this.getClient().checkFollowsBatch({ ...props })
            const entitiesList = response.entities || []
            return entitiesList.map((listItem) => ({
                entityType: listItem.entityType,
                entityId: listItem.entityId,
            })) as BaseEntity[]
        } catch (e: unknown) {
            console.error(e)
            throw this.handleError(e)
        }
    }

    private static handleError(e: unknown): NotificationServiceError {
        if (e instanceof Error) {
            const err = e as NotificationServiceError
            err.type = e.name
            return err
        }
        const err = new Error(
            'Unexpected error encountered, please try again later.'
        ) as NotificationServiceError
        err.type = 'UnknownError'
        return err
    }
}
